import './styles.sass';

import React from 'react';
import Transition from 'components/Explorer/Transition';
import { Link, useNavigate } from 'react-router-dom';

import NumberHelper from 'util/number_helper';
import MentionSourceProfileIcon from 'components/Explorer/ContentPanel/MentionSourceProfileIcon';
import TopItemList from 'components/Highlights/TopItemList';
import * as QueryParams from 'components/Explorer/models/query_params';
import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';

const linkForMentionSource = (params, mentionSource) => {
  const filters = { ...(params.filters || {}), 'mention_sources[]': [mentionSource.id] };
  return QueryParams.locationFor(QueryParams.withFilters(params, filters), '/mentions');
};

const trackJumpLink = () => {
  Analytics.trackEvent('highlights-click-jump-link', { jumpedTo: 'top-mentioners' });
};

const TopMentionSources = () => {
  const params = useQueryParams();
  const navigate = useNavigate();
  const { data, loading } = useExplorerData('highlights/top_mention_sources', params, { reloadOn: ['filters'] });

  const isLoading = loading === Loading.NOT_STARTED || loading === Loading.IN_PROGRESS;
  const hasResults = Array.isArray(data) && data.length > 0;

  const row = (mentionSource) => {
    const typeStr = I18n.t(`Explorer.Highlights.TopMentionSources.mention_source_types.${mentionSource.profileType}`);
    const target = linkForMentionSource(params, mentionSource);

    // Make the icon/whitespace navigate, but let the inner <Link> handle its
    // own click natively — otherwise the bubbled click would call navigate()
    // on top of the link's own navigation (double history push, double events).
    const handleRowClick = (event) => {
      if (event.target.closest('a, button')) return;
      navigate(target);
    };

    return (
      <TopItemList.Item key={mentionSource.id} onClick={handleRowClick}>
        <div>
          <MentionSourceProfileIcon profileType={mentionSource.profileType} />
        </div>
        <div className="mention-source-data">
          <span className="name">
            <Link to={target}>{mentionSource.name}</Link>
          </span>
          <span className="meta">
            {I18n.htmlSafe('Explorer.Highlights.TopMentionSources.meta', {
              count: mentionSource.mentions,
              formattedCount: NumberHelper.formatNumberWithDelimiter(mentionSource.mentions),
              source: typeStr
            })}
          </span>
        </div>
      </TopItemList.Item>
    );
  };

  const content = () => {
    if (loading === Loading.ERROR) {
      return <div className="Highlights-error-message">{I18n.t('Explorer.loading_error')}</div>;
    }
    return (
      <div key="content" className="Highlights-content Highlights-TopMentionSources-content">
        {hasResults ? (
          <TopItemList>{data.map(row)}</TopItemList>
        ) : (
          <div className="Highlights-content-empty-message">
            {I18n.t('Explorer.Highlights.TopMentionSources.empty')}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="Highlights-TopMentionSources Highlights-block">
      <h2 className="Highlights-title">{I18n.t('Explorer.Highlights.TopMentionSources.title')}</h2>
      <div className="Highlights-content-wrapper">
        <Transition>
          {isLoading ? <div key="loadingMessage" className="Highlights-loading-message" /> : content()}
        </Transition>
        {hasResults && (
          <Link
            onClick={trackJumpLink}
            to={QueryParams.locationFor(params, '/mention_sources')}
            className="Highlights-jump-link"
          >
            {I18n.t('Explorer.Highlights.TopMentionSources.more')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default TopMentionSources;
